"use client";
import { XIcon } from "lucide-react";
import useTranslate from "@app/_hooks/useTranslate";

import RichText from "@app/_blocks/RichText";
import { useCartState } from "@app/_context";
import BoxIcon from "@app/_icons/BoxIcon/BoxIcon";
import CheckIconLarge from "@app/_icons/CheckIconLarge/CheckIconLarge";
import { Dialog } from "@headlessui/react";
import type { ProductPageInfoBlockFragment } from "@server/gql/graphql";
import { AnimatePresence, m } from "framer-motion";
import type { ComponentProps } from "react";
import { useMemo, useState } from "react";

import type { MarketConfiguration } from "@/config/markets/const";
import { cn } from "@/lib/utils";

export default function ProductPageFreeShippingUspItem({
  className,
  marketConfig,
  usp,
  currentVariantPrice,
  ...props
}: ComponentProps<"button"> & {
  marketConfig: MarketConfiguration;
  usp: ProductPageInfoBlockFragment;
  currentVariantPrice: number;
}) {
  const [open, setOpen] = useState(false);
  const { cart } = useCartState();

  const cartTotal = useMemo(() => {
    return cart?.totalGrossAmount ?? 0;
  }, [cart?.totalGrossAmount]);

  const willReachFreeShipping = useMemo(() => {
    return cartTotal + currentVariantPrice > marketConfig.freeShippingThreshold;
  }, [marketConfig.freeShippingThreshold, currentVariantPrice, cartTotal]);

  const hasReachedFreeShipping = useMemo(() => {
    return cartTotal >= marketConfig.freeShippingThreshold;
  }, [cartTotal, marketConfig.freeShippingThreshold]);

  return (
    <li className={cn("flex items-start", className)}>
      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        className="relative z-50"
      >
        <div className="fixed inset-0 bg-black/30" aria-hidden="true" />
        <div className="fixed inset-0 flex items-center justify-center p-4">
          <Dialog.Panel className="prose-hr:my-6 relative mx-auto max-h-full max-w-xl items-start overflow-auto bg-white px-8 py-8 text-left md:py-12">
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="absolute top-2 right-0 p-4 outline-none"
            >
              <XIcon strokeWidth={1} className="size-4.5" />
            </button>
            <RichText
              content={usp.content}
              market={marketConfig.code}
              locale={marketConfig.locale}
            />
          </Dialog.Panel>
        </div>
      </Dialog>
      <button
        className="flex grow items-start gap-4 p-4 text-left whitespace-break-spaces"
        onClick={() => setOpen(true)}
        {...props}
      >
        <div className="relative h-5 w-5 overflow-hidden">
          <AnimatePresence mode="wait" initial={false}>
            {willReachFreeShipping || hasReachedFreeShipping ? (
              <m.div
                key="check"
                initial={{ y: "-100%" }}
                animate={{ y: 0 }}
                exit={{ y: "-100%" }} // Moves up when disappearing
                transition={{
                  duration: 0.25,
                  ease: "easeOut",
                }}
                className="absolute"
              >
                <CheckIconLarge className="size-5 shrink-0 text-green-500" />
              </m.div>
            ) : (
              <m.div
                key="box"
                initial={{ y: "100%" }}
                animate={{ y: 0 }}
                exit={{ y: "100%" }} // Moves down when disappearing
                transition={{
                  duration: 0.25,
                  ease: "easeOut",
                }}
                className="absolute"
              >
                <BoxIcon className="size-5 shrink-0 text-black" />
              </m.div>
            )}
          </AnimatePresence>
        </div>
        <AnimatePresence mode="wait" initial={false}>
          <UspItemAnimation
            usp={usp}
            hasReachedFreeShipping={hasReachedFreeShipping}
            willReachFreeShipping={willReachFreeShipping}
          />
        </AnimatePresence>
      </button>
    </li>
  );
}

function UspItemAnimation({
  usp,
  willReachFreeShipping,
  hasReachedFreeShipping,
}: {
  usp: ProductPageInfoBlockFragment;
  willReachFreeShipping: boolean;
  hasReachedFreeShipping: boolean;
}) {
  const t = useTranslate();
  if (hasReachedFreeShipping) {
    return (
      <m.div
        key="hasReachedFreeShippingMessage"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.25, ease: "linear" }}
      >
        {t("hasReachedFreeShippingMessage") ??
          "Du har uppnått fri frakt på din order!"}
      </m.div>
    );
  }

  if (willReachFreeShipping) {
    return (
      <m.div
        key="freeShipppingMessage"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.25, ease: "linear" }}
      >
        {t("freeShippingMessage") ??
          "Den här produkten ger dig fri frakt på din order"}
      </m.div>
    );
  }

  return (
    <m.div
      key="uspTitle"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.25, ease: "linear" }}
    >
      {usp.title}
    </m.div>
  );
}
